/**
 * Word filter for custom cosmetic text (calling card line, victory text). Builds on the shared
 * name / chat profanity list (src/shared/names.ts) and adds the checks custom creations need:
 * spaced-out words ("f u c k"), links and contact details are refused, not masked, so a custom
 * creation never shows a masked text. Player reports (moderation queue) catch what slips through.
 */
import { cleanText, containsProfanity, graphemeLength } from "../names";

export type TextRefusal = "empty" | "tooLong" | "blocked" | "link";

export type TextCheck = { ok: true; text: string } | { ok: false; reason: TextRefusal };

/** Links, e-mail addresses, handles and long digit runs (phone numbers) are not allowed. */
const LINK_RE = /(https?:|www\.|\.(com|net|org|nl|io|gg|be|de|ru|xyz)\b|@[a-z0-9_]{2,}|discord\.gg|\d{6,})/i;

/** Join single letters separated by spaces / dots ("f u c k" -> "fuck") for a second check. */
function squashSpacedLetters(text: string): string {
  return text.replace(/\b(?:\p{L}[\s._\-*]+){2,}\p{L}\b/gu, (m) => m.replace(/[\s._\-*]+/g, ""));
}

/**
 * Check a custom text. `allowEmpty` lets an editor clear the text (an empty text is then valid).
 * Returns the cleaned text (invisible characters removed, spaces collapsed) when it is allowed.
 */
export function checkCustomText(raw: unknown, maxLength: number, allowEmpty = true): TextCheck {
  const text = cleanText(raw);
  if (text.length === 0) return allowEmpty ? { ok: true, text: "" } : { ok: false, reason: "empty" };
  if (graphemeLength(text) > maxLength) return { ok: false, reason: "tooLong" };
  if (LINK_RE.test(text)) return { ok: false, reason: "link" };
  if (containsProfanity(text) || containsProfanity(squashSpacedLetters(text))) {
    return { ok: false, reason: "blocked" };
  }
  return { ok: true, text };
}

/** Max length of the calling card line. */
export const CARD_TEXT_MAX = 24;
/** Max length of the custom victory text. */
export const VICTORY_TEXT_MAX = 40;
